import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, FormControl, Autocomplete, CircularProgress, Chip, Button, ToggleButtonGroup, ToggleButton, IconButton, InputLabel, Select, MenuItem, Card, CardMedia, CardContent, CardActions } from '@mui/material';
import { FormatBold, FormatItalic, FormatStrikethrough, Visibility, Edit, Delete, Image, VideoFile, AudioFile, Description, AttachFile, ErrorOutline } from '@mui/icons-material';
import { API_BASE_URL } from 'envConfig';
import { apiClient } from '../../../utils/ApiClient';
import { Logger } from '../../../utils/Logger';

type MediaAttachment = {
  type: 'image' | 'video' | 'document' | 'audio';
  filename: string;
  mimeType: string;
  size: number;
  data?: string; // Base64 string
  localUrl?: string; // Blob URL for preview
  isOrphaned?: boolean; // Flag for error handling
  isDeleted?: boolean; // Flag for deletion tracking
  isNew?: boolean; // Flag for new uploads to persist on save
  originalName?: string; // Original filename
};

type Props = {
  currentTemplate: any;
  setCurrentTemplate: (fn: any) => void;
  dataSets: any[];
  templateNameError: string;
  setTemplateNameError: (e: string) => void;
  companyId: string;
  getDisplayCollectionName: (name?: string) => string;
  onSaveError?: (error: string) => void;
  isEditing?: boolean;
  /** When false, clears inline save errors (parent dialog closed). Defaults to true. */
  dialogOpen?: boolean;
};

export const WhatsAppTemplate: React.FC<Props> = ({
  currentTemplate,
  setCurrentTemplate,
  dataSets,
  templateNameError,
  setTemplateNameError,
  companyId,
  getDisplayCollectionName,
  onSaveError,
  isEditing = false,
  dialogOpen = true,
}) => {
  const excludedFields = ['_id', 'companyId', '__v', 'templateCreatedByUserId', 'templateUpdatedByUserId', 'templateUpdatedOn', 'createdAt', 'updatedAt', 'sharedTo'];
  const nameRegex = /^[a-zA-Z0-9_-]*$/;

  const [textValue, setTextValue] = useState<string>(String(currentTemplate?.templateContent || ''));
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
  const [lastFetchedDataSet, setLastFetchedDataSet] = useState<string>('');
  const [mediaAttachments, setMediaAttachments] = useState<MediaAttachment[]>([]);
  const [mediaLoadErrors, setMediaLoadErrors] = useState<Set<string>>(new Set());
  const [uploadingMedia, setUploadingMedia] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');

  useEffect(() => {
    if (dialogOpen === false) {
      setSaveError('');
      onSaveError?.('');
    }
  }, [dialogOpen, onSaveError]);

  useEffect(() => {
    setTextValue(String(currentTemplate?.templateContent || ''));
  }, [currentTemplate?.templateContent]);

  const getDisplayTemplateName = (fullTemplateName?: string) => {
    if (!fullTemplateName) return '';
    const parts = fullTemplateName.split('-');
    if (parts.length >= 3 && parts[0] === 'IUT' && parts[1] === companyId) {
      return parts.slice(2).join('-');
    }
    return fullTemplateName;
  };

  const fetchDataSetColumns = async (dataSetId: string) => {
    if (!dataSetId) {
      setAvailableColumns([]);
      setLastFetchedDataSet('');
      return;
    }
    if (lastFetchedDataSet === dataSetId) return;
    try {
      setLoadingColumns(true);
      const selected = dataSets.find(ds => ds._id === dataSetId);
      if (!selected) return;
      const full = selected.collectionName;
      const parts = full.split('-');
      const base = parts.length >= 3 ? parts.slice(2).join('-') : full;
      const resp = await fetch(`${API_BASE_URL}/iu/ds/cmp/set/entry/schema/${encodeURIComponent(base)}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('tokenid')}` }});
      const json = await resp.json();
      const schema = json.data || json;
      const cols = Object.keys(schema.paths || {}).filter(k => !['_id', 'createdAt', 'updatedAt', '__v'].includes(k));
      setAvailableColumns(cols);
      setLastFetchedDataSet(dataSetId);
    } catch {
      setAvailableColumns([]);
      setLastFetchedDataSet('');
    } finally {
      setLoadingColumns(false);
    }
  };

  useEffect(() => {
    if (currentTemplate?.collectionId) fetchDataSetColumns(currentTemplate.collectionId);
    else setAvailableColumns([]);
  }, [currentTemplate?.collectionId, dataSets]);

  const applyFormatting = (format: 'bold' | 'italic' | 'strikethrough') => {
    if (!selection || selection.start === selection.end) return;
    const before = textValue.substring(0, selection.start);
    const selected = textValue.substring(selection.start, selection.end);
    const after = textValue.substring(selection.end);
    const formatted = format === 'bold' ? `*${selected}*` : format === 'italic' ? `_${selected}_` : `~${selected}~`;
    const newText = before + formatted + after;
    setTextValue(newText);
    setCurrentTemplate((prev: any) => prev ? { ...prev, templateContent: newText } : null);
  };

  const formatWhatsAppMessage = (text: string) => {
    return (text || '')
      .replace(/\*([^*]+)\*/g, '<strong>$1<\/strong>')
      .replace(/_([^_]+)_/g, '<em>$1<\/em>')
      .replace(/~([^~]+)~/g, '<del>$1<\/del>')
      .replace(/```([^`]+)```/g, '<code>$1<\/code>')
      .replace(/^> (.+)$/gm, '<blockquote>$1<\/blockquote>')
      .replace(/\n/g, '<br>');
  };

  // Initialize media attachments only when template is first loaded for editing
  useMemo(() => {
    // Only initialize media attachments when we're editing and have a template with media
    if (isEditing && currentTemplate?._id && currentTemplate?.mediaAttachments && currentTemplate.mediaAttachments.length > 0) {
      Logger.debug('WhatsAppTemplate', `Initializing media attachments for existing template: ${currentTemplate.mediaAttachments.length} attachments`);
      
      // Filter out invalid attachments (those missing required fields)
      const validAttachments = currentTemplate.mediaAttachments.filter((att: any) => {
        const isValid = att && att.filename && att.mimeType && att.type;
        if (!isValid) {
          Logger.warn('WhatsAppTemplate', `Skipping invalid attachment: ${JSON.stringify(att)}`);
        }
        return isValid;
      });
      
      Logger.debug('WhatsAppTemplate', `Valid media attachments: ${validAttachments.length}`);
      
      // For existing templates, don't include the base64 data to avoid large payloads
      const attachmentsWithoutData = validAttachments.map((att: any) => ({
        ...att,
        data: undefined, // Remove base64 data for existing attachments
        localUrl: undefined // Remove local URL for existing attachments
      }));
      setMediaAttachments(attachmentsWithoutData);
    } else if (isEditing && currentTemplate?._id && (!currentTemplate?.mediaAttachments || currentTemplate.mediaAttachments.length === 0)) {
      // For existing templates with no media attachments, clear the list
      Logger.debug('WhatsAppTemplate', 'Existing template with no media attachments - clearing list');
      setMediaAttachments([]);
    }
    // Don't reset media attachments for new templates or when not editing
  }, [isEditing, currentTemplate?._id]);

  // Cleanup blob URLs when component unmounts or attachments change
  useEffect(() => {
    return () => {
      // Cleanup blob URLs when component unmounts
      mediaBlobUrls.forEach(url => {
        URL.revokeObjectURL(url);
      });
      setMediaBlobUrls(new Map());
    };
  }, []);

  // Cleanup blob URLs when media attachments change
  useEffect(() => {
    return () => {
      mediaBlobUrls.forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [mediaAttachments]);

  // Pre-fetch media for existing templates
  useEffect(() => {
    if (isEditing && currentTemplate?._id && mediaAttachments.length > 0) {
      // Pre-fetch all media attachments for existing templates (skip deleted)
      mediaAttachments.forEach(attachment => {
        if (!attachment.isDeleted && !attachment.localUrl && !attachment.data && !attachment.isOrphaned) {
          fetchMediaAsBlob(attachment);
        }
      });
    }
  }, [isEditing, currentTemplate?._id, mediaAttachments]);

  const handleMediaUpload = async (file: File) => {
    try {
      setUploadingMedia(true);
      setUploadError(''); // Clear any previous errors
      Logger.debug('WhatsAppTemplate', `Starting media upload for file: ${file.name}, Size: ${file.size}`);
      
      // Check file size before upload (16MB limit)
      const maxSize = 16 * 1024 * 1024; // 16MB in bytes
      if (file.size > maxSize) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
        throw new Error(`File size (${sizeInMB}MB) exceeds the maximum allowed size of 16MB. Please choose a smaller file.`);
      }
      
      // Always upload to server first, even for new templates
      const formData = new FormData();
      formData.append('media', file);
      formData.append('companyId', companyId);
      if (currentTemplate?._id) {
        formData.append('templateId', currentTemplate._id);
      }

      // For file uploads, use direct fetch with proper authentication headers
      // ApiClient is designed for JSON data, not FormData
      const token = localStorage.getItem('tokenid');
      const response = await fetch(`${API_BASE_URL}/iu/template/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Upload failed: ${response.status}`);
      }

      const result = await response.json();
      
      // The backend wraps the response in a data field
      const responseData = result.data || result;

      // Create a local URL for preview
      const localUrl = URL.createObjectURL(file);
      
      const newAttachment: MediaAttachment = {
        type: responseData.type, // This is 'image', 'video', 'document', or 'audio'
        filename: responseData.filename,
        originalName: file.name,
        mimeType: responseData.mimeType,
        size: responseData.size,
        data: responseData.data, // Store base64 data from server response
        isNew: true, // mark as new so save can include it
        localUrl // Set local preview URL
      };

      const updatedAttachments = [...mediaAttachments, newAttachment];
      setMediaAttachments(updatedAttachments);
      setCurrentTemplate((prev: any) => prev ? { ...prev, mediaAttachments: updatedAttachments } : null);

    } catch (error) {
      Logger.error('WhatsAppTemplate', `Media upload failed: ${error}`);
      setUploadError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleMediaDelete = (filename: string) => {
    const updatedAttachments = mediaAttachments.map(att =>
      att.filename === filename ? { ...att, isDeleted: true } : att
    );
    setMediaAttachments(updatedAttachments);
    setCurrentTemplate((prev: any) => prev ? { 
      ...prev, 
      mediaAttachments: updatedAttachments
    } : null);
  };

  const [mediaBlobUrls, setMediaBlobUrls] = useState<Map<string, string>>(new Map());
  const [mediaLoadingStates, setMediaLoadingStates] = useState<Map<string, boolean>>(new Map());

  // Function to fetch media with authentication and convert to blob URL
  const fetchMediaAsBlob = async (attachment: MediaAttachment): Promise<string | null> => {
    if (!currentTemplate?._id || attachment.isOrphaned) {
      return null;
    }

    // Check if already loading or loaded
    if (mediaLoadingStates.get(attachment.filename) || mediaBlobUrls.has(attachment.filename)) {
      return mediaBlobUrls.get(attachment.filename) || null;
    }

    try {
      // Set loading state
      setMediaLoadingStates(prev => new Map(prev).set(attachment.filename, true));

      const token = localStorage.getItem('tokenid');
      const mediaUrl = `${API_BASE_URL}/iu/template/media/${currentTemplate._id}/${attachment.filename}`;
      
      const response = await fetch(mediaUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        Logger.warn('WhatsAppTemplate', `Failed to fetch media: ${response.status} ${response.statusText}`);
        return null;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Store the blob URL in our map
      setMediaBlobUrls(prev => new Map(prev).set(attachment.filename, blobUrl));
      
      return blobUrl;
    } catch (error) {
      Logger.error('WhatsAppTemplate', `Error fetching media: ${error}`);
      return null;
    } finally {
      // Clear loading state
      setMediaLoadingStates(prev => {
        const newMap = new Map(prev);
        newMap.delete(attachment.filename);
        return newMap;
      });
    }
  };

  const getMediaUrl = (attachment: MediaAttachment): string => {
    // For attachments with localUrl (for preview)
    if (attachment.localUrl) {
      return attachment.localUrl;
    }
    
    // For attachments with base64 data
    if (attachment.data) {
      return `data:${attachment.mimeType || 'image/jpeg'};base64,${attachment.data}`;
    }
    
    // For existing attachments, check if we have a blob URL
    if (currentTemplate?._id && !attachment.isOrphaned) {
      const blobUrl = mediaBlobUrls.get(attachment.filename);
      if (blobUrl) {
        return blobUrl;
      }
      
      // If no blob URL exists, fetch it asynchronously
      fetchMediaAsBlob(attachment);
      return ''; // Return empty string while fetching
    }
    
    // Return empty string for orphaned or invalid attachments
    return '';
  };

  const handleMediaLoadError = (filename: string) => {
    // Check if this is a 404 error (file not found) or network error
    const is404Error = filename && (
      filename.includes('404') || 
      filename.includes('Failed to fetch') ||
      filename.includes('NetworkError') ||
      filename.includes('localhost:8080') || // Likely 404 from our backend
      filename.includes('iu/template/media') // Our media endpoint
    );
    
    if (is404Error) {
      // Silent handling for orphaned files - just mark as orphaned without console noise
      setMediaAttachments(prev => prev.map(att => 
        att.filename === filename 
          ? { ...att, isOrphaned: true }
          : att
      ));
      
      // Remove from error set since we're handling it gracefully
      setMediaLoadErrors(prev => {
        const newSet = new Set(prev);
        newSet.delete(filename || 'unknown');
        return newSet;
      });
    } else {
      // Only log non-404 errors for debugging
      Logger.error('WhatsAppTemplate', `Error loading media: ${filename}`);
      
      // Add to error set for other types of errors
      setMediaLoadErrors(prev => new Set(prev).add(filename || 'unknown'));
    }
  };

  const handleSaveError = (error: string) => {
    setSaveError(error);
    if (onSaveError) {
      onSaveError(error);
    }
  };

  const clearSaveError = () => {
    setSaveError('');
  };

  const handleTextFieldChange = (key: string, value: string) => {
    if (key === 'templateContent') setTextValue(value);
    setCurrentTemplate((prev: any) => prev ? { ...prev, [key]: value } : null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Save Error Display */}
      {saveError && (
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="body2" 
            color="error" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              padding: 2,
              borderRadius: 1,
              border: '1px solid rgba(244, 67, 54, 0.3)'
            }}
          >
            <ErrorOutline sx={{ fontSize: 20 }} />
            {saveError}
          </Typography>
        </Box>
      )}

      {/* templateName, templateType, accessLevel */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        <TextField
          size="small"
          label="WhatsApp Template Name"
          fullWidth
          value={getDisplayTemplateName(currentTemplate?.templateName)}
          onChange={(e) => {
            const value = e.target.value;
            if (!nameRegex.test(value)) setTemplateNameError('Only alphabets, numbers, underscore, and hyphen are allowed.');
            else setTemplateNameError('');
            setCurrentTemplate((prev: any) => prev ? { ...prev, templateName: `IUT-${companyId}-${value}` } : null);
          }}
          error={!!templateNameError}
          helperText={templateNameError}
        />
        <TextField size="small" label="Template Type" value="WHATSAPP" fullWidth disabled />
        <FormControl fullWidth>
          <InputLabel>Access Level</InputLabel>
          <Select size="small" label="Access Level" value={currentTemplate?.accessLevel || ''} onChange={(e) => setCurrentTemplate((prev: any) => prev ? { ...prev, accessLevel: e.target.value } : null)}>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Category & Keywords */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <TextField
          select
          size="small"
          label="Category"
          value={currentTemplate?.category || ''}
          onChange={(e) => setCurrentTemplate((prev: any) => prev ? { ...prev, category: e.target.value || undefined } : null)}
          fullWidth
          SelectProps={{
            displayEmpty: true,
            renderValue: (v: unknown) => {
              const val = v as string;
              if (!val) return <Typography variant="body2" color="text.secondary">Select category</Typography>;
              const cats = [
                { value: 'transactional', label: 'Transactional' },
                { value: 'promotional', label: 'Promotional' },
                { value: 'service', label: 'Service' },
                { value: 'system', label: 'System' },
              ];
              return cats.find(c => c.value === val)?.label ?? val;
            },
          }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          <MenuItem value="transactional">Transactional</MenuItem>
          <MenuItem value="promotional">Promotional</MenuItem>
          <MenuItem value="service">Service</MenuItem>
          <MenuItem value="system">System</MenuItem>
        </TextField>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={currentTemplate?.keywords || []}
          onChange={(_e: any, newValue: string[]) => setCurrentTemplate((prev: any) => prev ? { ...prev, keywords: newValue } : null)}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
        />
      </Box>

      {/* DataSet */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 2, mt: 2 }}>
        <FormControl fullWidth>
          <Autocomplete size="small" options={dataSets}
            getOptionLabel={(o) => getDisplayCollectionName(o?.collectionName)}
            value={dataSets.find((d) => d._id === currentTemplate?.collectionId) || null}
            onChange={(_, nv) => setCurrentTemplate((prev: any) => prev ? { ...prev, collectionId: nv?._id } : null)}
            renderInput={(params) => <TextField {...params} label="Select DataSet" />}
          />
        </FormControl>
      </Box>

      {currentTemplate?.collectionId && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Available Variables
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Use these variables in your template content with double curly braces:
          </Typography>
          {loadingColumns ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading columns...
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                p: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Chip
                key="consentURL"
                label="{{consentURL}}"
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
              {availableColumns.map((column) => (
                <Chip
                  key={column}
                  label={`{{${column}}}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Template Content *</Typography>
          <Button
            size="small"
            variant={showPreview ? "contained" : "outlined"}
            startIcon={showPreview ? <Edit /> : <Visibility />}
            onClick={() => setShowPreview(!showPreview)}
            sx={{
              backgroundColor: showPreview ? '#00a884' : 'transparent',
              color: showPreview ? '#ffffff' : '#00a884',
              borderColor: '#00a884',
              '&:hover': {
                backgroundColor: showPreview ? '#008f72' : 'rgba(0, 168, 132, 0.1)'
              }
            }}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
        </Box>

        {showPreview ? (
          <Box sx={{ 
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
            borderRadius: '8px',
            padding: '16px',
            background: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f0f2f5',
            minHeight: '200px'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              WhatsApp Message Preview
            </Typography>
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 2,
              flexDirection: 'column',
              alignItems: 'flex-end'
            }}>
              {/* Media Attachments */}
              {mediaAttachments.filter(attachment => attachment && attachment.mimeType && !attachment.isDeleted && !attachment.isOrphaned).map((attachment, index) => (
                <Box key={index} sx={{
                  maxWidth: '70%',
                  background: '#00a884',
                  borderRadius: '18px 18px 4px 18px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  {attachment.mimeType?.startsWith('image/') && (
                    <Box sx={{ position: 'relative' }}>
                      {(() => {
                        const mediaUrl = getMediaUrl(attachment);
                        const hasError = mediaLoadErrors.has(attachment.filename || 'unknown');
                        const isLoading = mediaLoadingStates.get(attachment.filename || 'unknown');
                        
                        // If loading, show loading indicator
                        if (isLoading) {
                          return (
                            <Box sx={{ 
                              p: 2, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              gap: 1,
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              minHeight: '80px'
                            }}>
                              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                              <Typography variant="caption" sx={{ color: '#ffffff' }}>
                                Loading...
                              </Typography>
                            </Box>
                          );
                        }
                        
                        // If no valid URL or has error, show fallback
                        if (!mediaUrl || hasError) {
                          return (
                            <Box sx={{ 
                              p: 2, 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              minHeight: '80px'
                            }}>
                              <Image sx={{ color: '#ffffff', fontSize: 24 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                                  {attachment.filename || 'Unknown File'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                  Failed to load media
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }
                        
                        return (
                          <img 
                            src={mediaUrl}
                            alt={attachment.filename || 'Image'}
                            style={{
                              width: '100%',
                              maxHeight: '200px',
                              objectFit: 'cover'
                            }}
                            onError={() => handleMediaLoadError(attachment.filename)}
                          />
                        );
                      })()}
                    </Box>
                  )}
                  
                  {attachment.mimeType?.startsWith('video/') && (
                    <Box sx={{ position: 'relative' }}>
                      {(() => {
                        const mediaUrl = getMediaUrl(attachment);
                        const hasError = mediaLoadErrors.has(attachment.filename || 'unknown');
                        
                        // If no valid URL or has error, show fallback
                        if (!mediaUrl || hasError) {
                          return (
                            <Box sx={{ 
                              p: 2, 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              minHeight: '80px'
                            }}>
                              <VideoFile sx={{ color: '#ffffff', fontSize: 24 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                                  {attachment.filename || 'Unknown File'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                  Failed to load media
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }
                        
                        return (
                          <>
                            <video 
                              src={mediaUrl}
                              controls
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover'
                              }}
                              onError={() => handleMediaLoadError(attachment.filename)}
                            />
                          </>
                        );
                      })()}
                    </Box>
                  )}
                  
                  {attachment.mimeType?.startsWith('audio/') && (
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AudioFile sx={{ color: '#ffffff', fontSize: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                          {attachment.filename || 'Audio File'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Audio
                        </Typography>
                      </Box>
                      {(() => {
                        const mediaUrl = getMediaUrl(attachment);
                        if (mediaUrl) {
                          return (
                            <audio controls style={{ height: 32 }}>
                              <source src={mediaUrl} type={attachment.mimeType || 'audio/mpeg'} />
                            </audio>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  )}
                  
                  {!attachment.mimeType?.startsWith('image/') && !attachment.mimeType?.startsWith('video/') && !attachment.mimeType?.startsWith('audio/') && (
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description sx={{ color: '#ffffff', fontSize: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                          {attachment.filename}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          Document
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
              
              {/* Message Bubble */}
              <Box sx={{
                maxWidth: '70%',
                background: '#00a884',
                color: '#ffffff',
                padding: '8px 12px',
                borderRadius: '18px 18px 4px 18px',
                position: 'relative',
                wordWrap: 'break-word',
                '& strong': {
                  fontWeight: 'bold'
                },
                '& em': {
                  fontStyle: 'italic'
                },
                '& del': {
                  textDecoration: 'line-through'
                },
                '& code': {
                  fontFamily: 'monospace',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  padding: '2px 4px',
                  borderRadius: '4px'
                },
                '& blockquote': {
                  borderLeft: '3px solid rgba(255, 255, 255, 0.5)',
                  paddingLeft: '8px',
                  margin: '4px 0'
                }
              }}
              dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(textValue) }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
            borderRadius: '8px',
            overflow: 'hidden',
            width: '100%'
          }}>
            {/* WhatsApp-style toolbar */}
            <Box sx={{ 
              background: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5',
              borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
              padding: '8px 12px',
              display: 'flex',
              gap: '4px'
            }}>
              <ToggleButtonGroup size="small" sx={{ 
                '& .MuiToggleButton-root': { 
                  border: 'none', 
                  color: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f' 
                } 
              }}>
                <ToggleButton 
                  value="bold" 
                  onClick={() => applyFormatting('bold')}
                  sx={{ 
                    '&:hover': { color: '#00a884' },
                    '&.Mui-selected': { color: '#00a884', backgroundColor: 'rgba(0, 168, 132, 0.1)' }
                  }}
                >
                  <FormatBold fontSize="small" />
                </ToggleButton>
                <ToggleButton 
                  value="italic" 
                  onClick={() => applyFormatting('italic')}
                  sx={{ 
                    '&:hover': { color: '#00a884' },
                    '&.Mui-selected': { color: '#00a884', backgroundColor: 'rgba(0, 168, 132, 0.1)' }
                  }}
                >
                  <FormatItalic fontSize="small" />
                </ToggleButton>
                <ToggleButton 
                  value="strikethrough" 
                  onClick={() => applyFormatting('strikethrough')}
                  sx={{ 
                    '&:hover': { color: '#00a884' },
                    '&.Mui-selected': { color: '#00a884', backgroundColor: 'rgba(0, 168, 132, 0.1)' }
                  }}
                >
                  <FormatStrikethrough fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* WhatsApp-style text area */}
            <TextField
              multiline
              minRows={6}
              maxRows={10}
              value={textValue}
              onChange={(e) => handleTextFieldChange('templateContent', e.target.value)}
              onSelect={(e) => {
                const target = e.target as HTMLTextAreaElement;
                setSelection({
                  start: target.selectionStart,
                  end: target.selectionEnd
                });
              }}
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true,
                style: {
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  lineHeight: '1.4'
                }
              }}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: '150px',
                  color: (theme) => theme.palette.text.primary,
                  backgroundColor: (theme) => theme.palette.background.paper
                },
                '& .MuiInputBase-input': {
                  color: (theme) => theme.palette.text.primary,
                  backgroundColor: (theme) => theme.palette.background.paper
                },
                width: '100%'
              }}
            />
          </Box>
        )}
      </Box>

      {/* Media Attachments Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Media Attachments
        </Typography>
        
      {/* File Upload Button */}
      <Box sx={{ mb: 2 }}>
        <input
          type="file"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setUploadError(''); // Clear any previous errors when a new file is selected
              handleMediaUpload(file);
            }
            // Reset input
            e.target.value = '';
          }}
          style={{ display: 'none' }}
          id="media-upload-input"
          disabled={uploadingMedia}
        />
        <label htmlFor="media-upload-input">
          <Button
            variant="outlined"
            component="span"
            startIcon={uploadingMedia ? <CircularProgress size={16} /> : <AttachFile />}
            disabled={uploadingMedia}
            sx={{
              borderColor: '#00a884',
              color: '#00a884',
              '&:hover': {
                borderColor: '#008f72',
                backgroundColor: 'rgba(0, 168, 132, 0.1)'
              }
            }}
          >
            {uploadingMedia ? 'Uploading...' : 'Add Media'}
          </Button>
        </label>
        
        {/* Error Message Display */}
        {uploadError && (
          <Box sx={{ mt: 1 }}>
            <Typography 
              variant="caption" 
              color="error" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                padding: 1,
                borderRadius: 1,
                border: '1px solid rgba(244, 67, 54, 0.3)'
              }}
            >
              <ErrorOutline sx={{ fontSize: 16 }} />
              {uploadError}
            </Typography>
          </Box>
        )}
      </Box>

        {/* Media Attachments List */}
        {mediaAttachments.filter(attachment => attachment && attachment.mimeType && !attachment.isDeleted).length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {mediaAttachments.filter(attachment => attachment && attachment.mimeType && !attachment.isDeleted).map((attachment, index) => (
              <Card key={index} sx={{ 
                border: '1px solid #e9edef',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {attachment.mimeType?.startsWith('image/') && (
                  mediaLoadErrors.has(attachment.filename || 'unknown') ? (
                    <Box sx={{ 
                      height: 120, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5'
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Image sx={{ color: '#666', fontSize: 32, mb: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          Failed to load
                        </Typography>
                      </Box>
                    </Box>
                  ) : mediaLoadingStates.get(attachment.filename || 'unknown') ? (
                    <Box sx={{ 
                      height: 120, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5'
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={32} sx={{ color: '#00a884', mb: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          Loading...
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <CardMedia
                      component="img"
                      height="120"
                      image={getMediaUrl(attachment)}
                      alt={attachment.filename || 'Image'}
                      sx={{ objectFit: 'cover' }}
                      onError={() => handleMediaLoadError(attachment.filename)}
                    />
                  )
                )}
                
                {attachment.mimeType?.startsWith('video/') && (
                  <Box sx={{ position: 'relative', height: 120 }}>
                    <video
                      src={getMediaUrl(attachment)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={() => handleMediaLoadError(attachment.filename)}
                    />
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '40px',
                      height: '40px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{
                        width: 0,
                        height: 0,
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        borderLeft: '12px solid white',
                        marginLeft: '2px'
                      }} />
                    </Box>
                  </Box>
                )}
                
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {attachment.mimeType?.startsWith('image/') && <Image sx={{ color: '#00a884', fontSize: 20 }} />}
                    {attachment.mimeType?.startsWith('video/') && <VideoFile sx={{ color: '#00a884', fontSize: 20 }} />}
                    {attachment.mimeType?.startsWith('audio/') && <AudioFile sx={{ color: '#00a884', fontSize: 20 }} />}
                    {!attachment.mimeType?.startsWith('image/') && !attachment.mimeType?.startsWith('video/') && !attachment.mimeType?.startsWith('audio/') && <Description sx={{ color: '#00a884', fontSize: 20 }} />}
                    <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1 }}>
                      {attachment.originalName || 'Unknown File'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleMediaDelete(attachment.filename)}
                      sx={{ color: '#ff4444' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {attachment.mimeType ? attachment.mimeType.split('/')[0].charAt(0).toUpperCase() + attachment.mimeType.split('/')[0].slice(1) : 'Unknown'} • {((attachment.size || 0) / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WhatsAppTemplate;


